<template>
  <div
    class="tracking-link-picker"
    data-test="graphics-trackingLinkPicker"
  >
    <label class="sidebar-form-label">Tracking Link</label>

    <div
      ref="control"
      class="tracking-link-picker__control"
      :class="{
        'tracking-link-picker__control--open': isOpen,
        'tracking-link-picker__control--error': hasError,
        'tracking-link-picker__control--disabled': disabled,
      }"
      role="combobox"
      tabindex="0"
      :aria-disabled="disabled"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
      @keydown.esc.prevent="close"
    >
      <template v-if="selectedLink">
        <span class="fas fa-link tracking-link-picker__icon tracking-link-picker__icon--link" />

        <span class="tracking-link-picker__selected">
          <span
            class="tracking-link-picker__title"
            :title="selectedLink.description || 'Untitled link'"
          >
            {{ selectedLink.description || 'Untitled link' }}
          </span>
          <span class="tracking-link-picker__subtitle">{{ formatShortUrl(selectedLink) }}</span>
        </span>

        <span
          class="tracking-link-picker__tag"
          :style="typeTagStyle(selectedLink.type)"
        >
          {{ typeLabel(selectedLink.type) }}
        </span>
      </template>

      <template v-else>
        <span
          v-if="isLoading"
          class="tracking-link-picker__spinner"
        />
        <span
          v-else
          class="fas fa-magnifying-glass tracking-link-picker__icon"
        />

        <span
          class="tracking-link-picker__placeholder"
          :class="{ 'tracking-link-picker__placeholder--error': hasError }"
        >
          {{ placeholder }}
        </span>
      </template>

      <span
        class="fas fa-chevron-down tracking-link-picker__chevron"
        :class="{ 'tracking-link-picker__chevron--open': isOpen }"
      />
    </div>

    <p
      v-if="hasError"
      class="tracking-link-picker__error"
    >
      <span class="fas fa-triangle-exclamation" />
      Could not load tracking links. Try again or contact support.
    </p>

    <div
      v-if="isOpen"
      class="tracking-link-picker__menu"
    >
      <div class="tracking-link-picker__search-header">
        <div class="tracking-link-picker__search-wrapper">
          <span class="fas fa-magnifying-glass tracking-link-picker__search-icon" />
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            class="tracking-link-picker__search"
            :aria-activedescendant="
              highlightedLink ? `tracking-link-option-${highlightedLink.trackingLinkId}` : null
            "
            placeholder="Search by name or URL..."
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
            @keydown.enter.prevent="onEnter"
            @keydown.esc.prevent="close(true)"
          />
        </div>
      </div>

      <div
        ref="menu"
        class="tracking-link-picker__results"
        role="listbox"
      >
        <div
          v-if="isLoading"
          class="tracking-link-picker__state"
        >
          Loading tracking links...
        </div>

        <template v-else>
          <template v-if="!hasError && pageLinks.length">
            <div class="tracking-link-picker__group-label">
              Pages
            </div>

            <button
              v-for="(link, index) in pageLinks"
              :id="`tracking-link-option-${link.trackingLinkId}`"
              :key="link.trackingLinkId"
              type="button"
              class="tracking-link-picker__option"
              :class="{ 'tracking-link-picker__option--highlighted': index === highlightedIndex }"
              role="option"
              :aria-selected="index === highlightedIndex"
              :title="link.description || 'Untitled link'"
              @mouseenter="highlightedIndex = index"
              @focusin="highlightedIndex = index"
              @click="selectLink(link)"
            >
              <span class="tracking-link-picker__option-header">
                <span class="tracking-link-picker__title">
                  {{ link.description || 'Untitled link' }}
                </span>
                <span
                  class="tracking-link-picker__tag"
                  :style="typeTagStyle(link.type)"
                >
                  {{ typeLabel(link.type) }}
                </span>
              </span>
              <span
                class="tracking-link-picker__option-destination"
                :title="link.currentDestination && link.currentDestination.destinationUrl"
              >
                {{
                  stripProtocol(link.currentDestination && link.currentDestination.destinationUrl)
                }}
              </span>
              <span class="tracking-link-picker__subtitle">{{ formatShortUrl(link) }}</span>
            </button>
          </template>

          <template v-if="!hasError && customLinks.length">
            <hr
              v-if="pageLinks.length"
              class="tracking-link-picker__group-divider"
            >

            <div class="tracking-link-picker__group-label">
              Links
            </div>

            <button
              v-for="(link, index) in customLinks"
              :id="`tracking-link-option-${link.trackingLinkId}`"
              :key="link.trackingLinkId"
              type="button"
              class="tracking-link-picker__option"
              :class="{
                'tracking-link-picker__option--highlighted':
                  pageLinks.length + index === highlightedIndex,
              }"
              role="option"
              :aria-selected="pageLinks.length + index === highlightedIndex"
              :title="link.description || 'Untitled link'"
              @mouseenter="highlightedIndex = pageLinks.length + index"
              @focusin="highlightedIndex = pageLinks.length + index"
              @click="selectLink(link)"
            >
              <span class="tracking-link-picker__option-header">
                <span class="tracking-link-picker__title">
                  {{ link.description || 'Untitled link' }}
                </span>
                <span
                  class="tracking-link-picker__tag"
                  :style="typeTagStyle(link.type)"
                >
                  {{ typeLabel(link.type) }}
                </span>
              </span>
              <span
                class="tracking-link-picker__option-destination"
                :title="link.currentDestination && link.currentDestination.destinationUrl"
              >
                {{
                  stripProtocol(link.currentDestination && link.currentDestination.destinationUrl)
                }}
              </span>
              <span class="tracking-link-picker__subtitle">{{ formatShortUrl(link) }}</span>
            </button>
          </template>

          <div
            v-if="!hasError && !pageLinks.length && !customLinks.length"
            class="tracking-link-picker__state tracking-link-picker__empty"
          >
            <template v-if="search">
              <span class="fas fa-magnifying-glass tracking-link-picker__empty-icon" />
              <p class="tracking-link-picker__empty-title">
                No results for "{{ search }}"
              </p>
              <p class="tracking-link-picker__empty-subtitle">
                Try a different term or create a new link below.
              </p>
            </template>

            <template v-else>
              No tracking links found. Create one below.
            </template>
          </div>
        </template>
      </div>

      <a
        :href="assetManagerUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="tracking-link-picker__create"
        @click.prevent="openCreateTrackingLink"
      >
        <span class="fas fa-plus tracking-link-picker__create-icon" />
        <span>Create new tracking link</span>
      </a>
    </div>
  </div>
</template>

<script>
import TrackingLinks from '../../../services/tracking-links';
import { inIframe, postMessage } from '../../../utils/helpers';

const TRACKING_LINK_TYPE_STYLES = {
  custom: { backgroundColor: '#c2eff5', color: '#006775' },
  page: { backgroundColor: '#ffe6c2', color: '#8c5400' },
  shoppable: { backgroundColor: '#caf1d8', color: '#136c34' },
};

const FALLBACK_TYPE_STYLE = { backgroundColor: '#f1f5f9', color: '#334155' };

export default {
  name: 'TrackingLinkPicker',

  props: {
    modelValue: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'select'],

  data() {
    return {
      links: [],
      resolvedLink: null,
      searchTimer: null,
      linksRequestId: 0,
      isLoading: false,
      hasError: false,
      isOpen: false,
      search: '',
      loaded: false,
      highlightedIndex: -1,
      // Token used to correlate a create request with its reply from the parent
      // Creator app when this picker is embedded in an iframe editor.
      awaitingCreateToken: null,
    };
  },

  computed: {
    selectedLink() {
      return this.links.find((link) => link.rid === this.modelValue)
        || (this.resolvedLink?.rid === this.modelValue ? this.resolvedLink : null);
    },

    pageLinks() {
      return this.links.filter((link) => link.type === 'page');
    },

    customLinks() {
      return this.links.filter((link) => (
        link.type !== 'page' && link.type !== 'shoppable'
      ));
    },

    // Flat, visual-order list used for keyboard navigation (Pages then Links).
    visibleLinks() {
      if (this.isLoading || this.hasError) return [];
      return [...this.pageLinks, ...this.customLinks];
    },

    highlightedLink() {
      return this.visibleLinks[this.highlightedIndex] || null;
    },

    placeholder() {
      if (this.hasError) {
        return 'Failed to load tracking links';
      }

      return this.isLoading ? 'Loading tracking links...' : 'Select a tracking link...';
    },

    assetManagerUrl() {
      const appUrl = process.env.TAGBOARD_APP_URL
        || process.env.TAGLANDIA_URL
        || 'https://app.tagboard.com';
      return `${appUrl}/tracking-links`;
    },

    // Origins we accept the create-modal reply from. The reply must also arrive
    // from `window.parent` and carry the request token, so this is defense in
    // depth. We gather every parent origin we can discover because
    // `TAGBOARD_APP_URL` is not always set in local/dev builds.
    allowedParentOrigins() {
      const origins = new Set();

      try {
        const { ancestorOrigins } = window.location;

        if (ancestorOrigins && ancestorOrigins.length) {
          for (let i = 0; i < ancestorOrigins.length; i += 1) {
            origins.add(ancestorOrigins[i]);
          }
        }
      } catch (err) { /* ancestorOrigins unsupported */ }

      try {
        if (document.referrer) {
          origins.add(new URL(document.referrer).origin);
        }
      } catch (err) { /* referrer unavailable */ }

      try {
        origins.add(new URL(this.assetManagerUrl).origin);
      } catch (err) { /* invalid app url */ }

      return origins;
    },
  },

  watch: {
    modelValue() {
      this.resolveSelectedLink();
    },

    search() {
      // Invalidate in-flight results as soon as the query changes, including
      // while waiting for the next debounced request to start.
      this.linksRequestId += 1;
      clearTimeout(this.searchTimer);
      this.highlightedIndex = -1;
      this.isLoading = this.isOpen;
      this.hasError = false;
      this.loaded = false;
      if (!this.isOpen) return;
      this.searchTimer = setTimeout(() => {
        if (this.isOpen) this.loadLinks();
      }, 300);
    },
  },

  mounted() {
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('message', this.onMessage);

    if (this.modelValue) {
      this.resolveSelectedLink();
    }
  },

  beforeUnmount() {
    clearTimeout(this.searchTimer);
    this.linksRequestId += 1;
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('message', this.onMessage);
  },

  methods: {
    onDocumentClick(event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.close();
      }
    },

    typeLabel(type) {
      const labels = {
        page: 'Page',
        custom: 'Link',
        shoppable: 'Shoppable',
      };

      return labels[type] || 'Link';
    },

    typeTagStyle(type) {
      return TRACKING_LINK_TYPE_STYLES[type] || FALLBACK_TYPE_STYLE;
    },

    formatShortUrl(link) {
      if (!link || !link.rid) {
        return '';
      }

      const baseUrl = process.env.TGB_IO_URL || 'https://tgb.io';
      const host = baseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '').split('/')[0];

      return `${host}/t/${link.rid}`;
    },

    stripProtocol(url) {
      return (url || '').replace(/^https?:\/\//i, '');
    },

    /**
     * Ask the parent Creator app to open its create-tracking-link modal when
     * embedded in an editor iframe. Standalone usage falls back to the asset
     * manager in a new tab.
     */
    openCreateTrackingLink() {
      if (!inIframe()) {
        window.open(this.assetManagerUrl, '_blank');
        return;
      }

      const token = Math.random().toString(36).slice(2);

      this.awaitingCreateToken = token;
      postMessage(`create-tracking-link:${token}`);
    },

    // The Creator app replies with the created/reused link; select it here.
    onMessage(event) {
      // Only accept the reply from the embedding Creator app, not from any
      // other window that happens to know the request token.
      if (event.source !== window.parent) {
        return;
      }

      if (this.allowedParentOrigins.size && !this.allowedParentOrigins.has(event.origin)) {
        return;
      }

      const data = typeof event.data === 'string' ? event.data : '';
      const [, message, payload] = data.split(':');

      if (message !== 'tracking-link-created' && message !== 'tracking-link-reused') {
        return;
      }

      const [token, rid] = (payload || '').split('|');

      if (!this.awaitingCreateToken || token !== this.awaitingCreateToken) {
        return;
      }

      this.awaitingCreateToken = null;
      this.selectCreatedLink(rid);
    },

    async selectCreatedLink(rid) {
      if (!rid) {
        return;
      }

      await this.loadLinks();

      let link = this.links.find((item) => item.rid === rid);

      if (!link) {
        try {
          link = await TrackingLinks.getTrackingLinkByRid(rid);

          if (link && link.rid) {
            this.links = [link, ...this.links];
          }
        } catch (err) {
          console.error('Failed to resolve created tracking link:', err);
        }
      }

      if (link) {
        this.selectLink(link);
      }
    },

    async loadLinks() {
      clearTimeout(this.searchTimer);
      this.linksRequestId += 1;
      const requestId = this.linksRequestId;
      const search = this.search.trim();
      this.isLoading = true;
      this.hasError = false;
      this.loaded = false;
      this.highlightedIndex = -1;

      try {
        const resp = await TrackingLinks.getTrackingLinks({
          page: 1,
          limit: 100,
          ...(search ? { search } : {}),
          sortBy: 'updatedAt',
          sortDir: 'desc',
          // Shoppable links are managed by the Shoppable flow and never belong
          // in the picker. Also filter client-side so older APIs still behave.
          excludeTypes: 'shoppable',
          ...(this.type ? { type: this.type } : {}),
        });

        if (requestId !== this.linksRequestId) return;

        // Keep the current selection available even when it is absent from
        // the new search results, without adding it to the result list.
        this.resolvedLink = this.selectedLink;
        this.links = ((resp && resp.links) || [])
          .filter((link) => link.type !== 'shoppable');
        this.loaded = true;
        this.highlightedIndex = search ? 0 : -1;

        await this.resolveSelectedLink();
      } catch (err) {
        if (requestId !== this.linksRequestId) return;
        console.error('Failed to load tracking links:', err);
        this.hasError = true;
      } finally {
        if (requestId === this.linksRequestId) this.isLoading = false;
      }
    },

    /**
     * The list is paginated, so a selected link may not be in the loaded page.
     * Fetch it by rid so the control can render the current selection.
     */
    async resolveSelectedLink() {
      if (!this.modelValue || this.selectedLink) {
        return;
      }

      const rid = this.modelValue;
      try {
        const link = await TrackingLinks.getTrackingLinkByRid(rid);

        if (link && link.rid === this.modelValue && rid === this.modelValue) {
          this.resolvedLink = link;
        }
      } catch (err) {
        console.error('Failed to resolve selected tracking link:', err);
      }
    },

    toggle() {
      if (this.disabled) {
        return;
      }

      if (this.isOpen) {
        this.close();
        return;
      }

      this.isOpen = true;

      if (!this.loaded && !this.isLoading) {
        this.loadLinks();
      }

      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },

    selectLink(link, restoreFocus = false) {
      this.resolvedLink = link;
      this.$emit('update:modelValue', link.rid);
      this.$emit('select', link);
      this.close(restoreFocus);
    },

    scrollHighlightedIntoView() {
      this.$nextTick(() => {
        const option = this.$refs.menu?.querySelector('.tracking-link-picker__option--highlighted');

        option?.scrollIntoView({ block: 'nearest' });
      });
    },

    onArrowDown() {
      if (!this.visibleLinks.length) {
        return;
      }

      this.highlightedIndex = this.highlightedIndex >= this.visibleLinks.length - 1
        ? 0
        : this.highlightedIndex + 1;

      this.scrollHighlightedIntoView();
    },

    onArrowUp() {
      if (!this.visibleLinks.length) {
        return;
      }

      this.highlightedIndex = this.highlightedIndex <= 0
        ? this.visibleLinks.length - 1
        : this.highlightedIndex - 1;

      this.scrollHighlightedIntoView();
    },

    onEnter() {
      // Arrow navigation (or an active search) selects the highlighted option,
      // which defaults to the top result while typing.
      if (this.highlightedLink) {
        this.selectLink(this.highlightedLink, true);
        return;
      }

      // Search with no matches: stay open so the user can adjust.
      if (this.search.trim()) {
        return;
      }

      // Empty search, no navigation: keep the current selection.
      this.close(true);
    },

    close(restoreFocus = false) {
      clearTimeout(this.searchTimer);
      this.linksRequestId += 1;
      if (this.search.trim() || this.isLoading) this.loaded = false;
      this.isLoading = false;
      this.isOpen = false;
      this.search = '';
      this.highlightedIndex = -1;

      // Keyboard-triggered closures remove the focused search input, so return
      // focus to the combobox to keep the user's place.
      if (restoreFocus) {
        this.$nextTick(() => this.$refs.control?.focus());
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tracking-link-picker {
  --tracking-link-picker-create-height: 52px;

  position: relative;
  margin-bottom: 0;

  // Match the graphics editor's native input/select styling ($background-light,
  // $border-light, $border-radius, and the primary focus ring).
  &__control {
    display: flex;
    align-items: center;
    gap: 2px;
    width: 100%;
    min-height: 40px;
    padding: 6px;
    font-size: 14px;
    color: #38474b;
    background: #f2f6fa;
    border: 1px solid #dfeaef;
    border-radius: 6px;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(.tracking-link-picker__control--disabled) {
      border-color: #c5dbe4;
    }

    &--open {
      border-color: rgba(0, 180, 204, 0.5);
      box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 8px rgba(0, 180, 204, 0.6);
    }

    &--error {
      border-color: #f87171;
      box-shadow: 0 0 0 3px rgba(252, 165, 165, 0.2);
    }

    &--disabled {
      background-color: #dfeaef;
      color: #a1b0c0;
      cursor: not-allowed;
    }
  }

  &__icon {
    flex-shrink: 0;
    color: #98a2b3;

    &--link {
      color: #00b4cc;
    }
  }

  &__placeholder {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 13px;
    color: #98a2b3;

    &--error {
      color: #f87171;
    }
  }

  &__selected {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    line-height: 1.2;
  }

  &__title {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-weight: 500;
    color: #64748b;
  }

  &__subtitle {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-family: monospace;
    font-size: 12px;
    color: #99a1af;
  }

  &__option-destination {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100%;
    font-size: 12px;
    color: #99a1af;
  }

  &__tag {
    flex-shrink: 0;
    padding: 2px 6px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.1;
    border-radius: 9999px;
  }

  &__chevron {
    flex-shrink: 0;
    color: #98a2b3;
    transition: transform 0.2s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__spinner {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(0, 180, 204, 0.3);
    border-top-color: #00b4cc;
    border-radius: 50%;
    animation: tracking-link-picker-spin 0.6s linear infinite;
  }

  &__error {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 6px 0 0;
    font-size: 13px;
    color: #ef4444;
  }

  &__menu {
    position: absolute;
    z-index: 20;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    max-height: 352px;
    overflow: hidden;
    background: #ffffff;
    border: 1px solid #dfeaef;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
  }

  &__search-header {
    flex-shrink: 0;
    padding: 8px;
    background: #ffffff;
  }

  &__results {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  &__search-wrapper {
    display: flex;
    align-items: center;
    gap: 2px;
    min-height: 40px;
    padding: 6px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
  }

  &__search-icon {
    color: #98a2b3;
  }

  &__search {
    flex: 1;
    min-width: 0;
    padding: 0;
    font-size: 14px;
    color: #101828;
    background: transparent;
    border: none;
    outline: none;

    &:focus {
      background: transparent;
      border: none;
      outline: none;
      box-shadow: none;
    }

    &::placeholder {
      color: #98a2b3;
    }
  }

  &__group-label {
    padding: 8px 10px 4px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #99a1af;
  }

  &__group-divider {
    height: 0;
    margin: 8px 0;
    border: 0;
    border-top: 1px solid #dfeaef;
  }

  &__option {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    width: 100%;
    padding: 6px 10px;
    text-align: left;
    background: transparent;
    border: none;
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: #f9fafb;
    }

    &--highlighted {
      background-color: rgba(0, 180, 204, 0.16);
    }
  }

  &__option-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
  }

  &__state {
    padding: 12px 10px;
    font-size: 15px;
    color: #667085;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 32px 16px;
    text-align: center;
  }

  &__empty-icon {
    font-size: 24px;
    color: #98a2b3;
  }

  &__empty-title {
    margin: 0;
    font-weight: 500;
    color: #101828;
  }

  &__empty-subtitle {
    margin: 0;
    font-size: 13px;
    color: #98a2b3;
  }

  &__create {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: var(--tracking-link-picker-create-height);
    padding: 12px;
    background: #ffffff;
    border-top: 1px solid #f2f4f7;
    color: #00b4cc;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;

    &:hover {
      background-color: #f9fafb;
    }
  }

  &__create-icon {
    flex-shrink: 0;
  }
}

@keyframes tracking-link-picker-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
